/**
 * BlueprintsTemplatesTab — Templates tab for the Blueprints hub.
 *
 * ## What this file exports
 *
 * `BlueprintsTemplateListPage` (named export) — injected into the inline
 * `<ScaffolderPage>` in App.tsx via the
 * `components.EXPERIMENTAL_TemplateListPageComponent` prop. It renders the
 * grouped, searchable template grid when the user is at `/create/` (the root
 * templates list). The plugin router passes `TemplateListPageProps` to it,
 * but we fetch templates independently via `catalogApi` and build our own
 * curated-category grouping.
 *
 * There is NO default export from this file. `<ScaffolderPage>` itself is
 * mounted directly in App.tsx as a `<Route element>` inside `<FlatRoutes>` so
 * that Backstage's routable-extension discovery can find
 * `routeRef{type=absolute,id=scaffolder}` (needed by
 * `bind(catalogPlugin.externalRoutes, { createComponent: ... })`). Do NOT
 * wrap `<ScaffolderPage>` in another component and mount that at `/create`;
 * doing so hides the routeRef and breaks catalog → "Create Component".
 *
 * ## Sibling-route architecture
 *
 * The four other Blueprints pages are mounted as sibling routes in App.tsx,
 * BEFORE the `/create` catch-all, so React Router specificity lets them
 * intercept their exact paths:
 *
 *   /create/tasks         → TasksListPage        (our custom list)
 *   /create/actions       → ActionsCatalogPage   (our custom catalog)
 *   /create/custom-fields → CustomFieldsPage     (blueprintRoutes.tsx)
 *   /create/guide         → AuthoringGuidePage   (blueprintRoutes.tsx)
 *   /create               → ScaffolderPage (App.tsx) with
 *                              EXPERIMENTAL_TemplateListPageComponent=BlueprintsTemplateListPage
 *
 * /create/tasks/:taskId is owned by TasksListPage itself (it renders the OOTB
 * scaffolder TaskPage for that segment) — <FlatRoutes> appends `/*` to every
 * route path, so /create/tasks also matches /create/tasks/:taskId.
 *
 * ## Template grouping approach
 *
 * Fetch via catalogApi.getEntities (NOT from props.templates —
 * TemplateListPageProps does not carry a templates array; it provides group
 * filter functions and card component overrides). Each template is placed in
 * every section whose curated category id appears in metadata.tags. A template
 * with no curated category tags appears in the "Other" section.
 *
 * ## Header / tab consistency
 *
 * BlueprintsTemplateListPage renders its own <Page> + <BlueprintsHeader> with
 * <HeaderTabs>. The tab list is sourced from BlueprintsHeader.tsx (single
 * source of truth — BLUEPRINTS_TABS). All five Blueprints pages (Templates,
 * Task Runs, Actions, Custom Fields, Authoring Guide) present the same 5-tab
 * bar by importing BlueprintsHeader.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  ButtonLink,
  Container,
  Flex,
  FullPage,
  Grid,
  SearchField,
  Select,
  Skeleton,
  Text,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiGridLine,
  RiListCheck,
  RiPencilLine,
  RiShapesLine,
  RiSortAlphabetAsc,
} from '@remixicon/react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { BlueprintsHeader } from './BlueprintsHeader';
import { useBlueprintAnalytics } from './useBlueprintAnalytics';
import FavoritesAndRecent from './FavoritesAndRecent';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { useNavigate } from 'react-router-dom';
import { useRouteRef } from '@backstage/core-plugin-api';
import type { TemplateListPageProps } from '@backstage/plugin-scaffolder/alpha';
import type { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import {
  BLUEPRINT_CATEGORIES,
  type BlueprintCategory,
} from './blueprintCategories';
import { BlueprintCard, BlueprintListRow } from './BlueprintCard';

// Tab list and active-tab logic live in <BlueprintsHeader> (./BlueprintsHeader.tsx).

// ── Levenshtein for typo detection ────────────────────────────────────────────

/** Returns the edit distance between two strings. */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  // dp[i][j] = edit distance between a[0..i-1] and b[0..j-1]
  const dp: number[][] = Array.from({ length: m + 1 }, (_row, i) =>
    Array.from({ length: n + 1 }, (_col, j) => {
      if (i === 0) return j;
      if (j === 0) return i;
      return 0;
    }),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
}

// ── Type guard ────────────────────────────────────────────────────────────────

function isTemplateEntity(entity: {
  kind: string;
}): entity is TemplateEntityV1beta3 {
  return entity.kind === 'Template';
}

// ── View-preference persistence ───────────────────────────────────────────────

type ViewMode = 'grid' | 'list';
type Grouping = 'categorised' | 'alphabetical';

const VIEW_MODE_KEY = 'blueprints.templates.viewMode';
const GROUPING_KEY = 'blueprints.templates.grouping';

/**
 * useState variant that persists the value in localStorage under `key`.
 *
 * The stored value is validated against `allowed` on read so a stale or
 * tampered entry falls back to `initial` instead of producing an invalid UI
 * state. All localStorage access is wrapped in try/catch because it can throw
 * in private-mode / sandboxed browsers.
 */
function usePersistedChoice<T extends string>(
  key: string,
  allowed: readonly T[],
  initial: T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored !== null && (allowed as readonly string[]).includes(stored)) {
        return stored as T;
      }
    } catch {
      // Ignore — fall back to the default below.
    }
    return initial;
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, next);
      } catch {
        // Persistence is best-effort; ignore storage failures.
      }
    },
    [key],
  );

  return [value, set];
}

/** Display name used for searching and alphabetical sorting. */
function templateDisplayName(t: TemplateEntityV1beta3): string {
  return t.metadata.title ?? t.metadata.name;
}

/**
 * Safe accessor for a template's tags.
 *
 * `metadata.tags` is typed as `string[]`, but entity metadata is parsed from
 * user-authored YAML and a malformed `tags: onboarding` (scalar instead of a
 * list) can slip through as a non-array at runtime. Returning `[]` in that
 * case keeps array operations (`flatMap`/`some`/`every`/`map`) from throwing
 * and crashing the whole page.
 */
function tagsOf(t: TemplateEntityV1beta3): string[] {
  return Array.isArray(t.metadata.tags) ? t.metadata.tags : [];
}

/**
 * Templates tagged with this are intentionally kept out of the Blueprints grid.
 *
 * Used for templates that are only meant to run from a purpose-built in-app
 * flow (e.g. `configure-vibe-coded-app`, driven by the App setup card bound to
 * the app in view) rather than being discovered and free-picked here. This
 * only hides the card from the list; the template still exists and its direct
 * URL still resolves — enforcement of any run-time constraints lives in the
 * template's own steps.
 */
export const HIDDEN_BLUEPRINT_TAG = 'blueprints:hidden';

function isHiddenBlueprint(t: TemplateEntityV1beta3): boolean {
  return tagsOf(t).includes(HIDDEN_BLUEPRINT_TAG);
}

// ── Grouped template section ──────────────────────────────────────────────────

interface SectionProps {
  category:
    | BlueprintCategory
    | { id: string; label: string; description: string };
  templates: TemplateEntityV1beta3[];
  viewMode: 'grid' | 'list';
  onChoose: (t: TemplateEntityV1beta3) => void;
}

function TemplateSection({
  category,
  templates,
  viewMode,
  onChoose,
}: SectionProps) {
  const [open, setOpen] = useState(true);
  let content: JSX.Element;

  if (templates.length === 0) {
    content = (
      <Text as="p" color="secondary">
        No templates in this category yet.
      </Text>
    );
  } else if (viewMode === 'grid') {
    content = (
      <Grid.Root columns={{ initial: '1', sm: '2', md: '3' }} gap="4">
        {templates.map(t => (
          <BlueprintCard
            key={`${t.metadata.namespace ?? 'default'}/${t.metadata.name}`}
            template={t}
            onChoose={() => onChoose(t)}
          />
        ))}
      </Grid.Root>
    );
  } else {
    content = (
      <Flex direction="column" gap="2">
        {templates.map(t => (
          <BlueprintListRow
            key={`${t.metadata.namespace ?? 'default'}/${t.metadata.name}`}
            template={t}
            onChoose={() => onChoose(t)}
          />
        ))}
      </Flex>
    );
  }

  return (
    <Box mb="6">
      {/*
       * Collapsible header underlined in the brand colour, as on the original
       * page. A native disclosure button rather than BUI's Accordion, whose
       * panel surface would box each category instead of leaving the cards on
       * the page background.
       */}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          all: 'unset',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--bui-space-2)',
          width: '100%',
          padding: 'var(--bui-space-2) 0',
          marginBottom: 'var(--bui-space-4)',
          borderBottom: '2px solid var(--make-fg-brand)',
          cursor: 'pointer',
        }}
      >
        <Text as="span" variant="body-large" weight="bold">
          {category.label}
        </Text>
        {category.description && (
          <Text as="span" color="secondary">
            - {category.description}
          </Text>
        )}
        <Text as="span" color="secondary" style={{ marginLeft: 'auto' }}>
          {templates.length}
        </Text>
        {open ? (
          <RiArrowUpSLine size={16} aria-hidden />
        ) : (
          <RiArrowDownSLine size={16} aria-hidden />
        )}
      </button>

      {open && content}
    </Box>
  );
}

// ── List-mode row ─────────────────────────────────────────────────────────────

// ListRow now lives in ./BlueprintCard.tsx as `BlueprintListRow` (shared
// with FavoritesAndRecent so the view toggle affects every section).

// ── Custom template list page (EXPERIMENTAL) ──────────────────────────────────

/**
 * Custom replacement for ScaffolderPage's internal TemplateListPage.
 *
 * Receives `TemplateListPageProps` from ScaffolderPage but fetches templates
 * independently via catalogApi (TemplateListPageProps does not include a
 * templates array - it provides group filters and card overrides which we
 * override with our own grouped implementation).
 */
export function BlueprintsTemplateListPage(
  _props: TemplateListPageProps,
): JSX.Element {
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();
  const selectedTemplateRoute = useRouteRef(
    scaffolderPlugin.routes.selectedTemplate,
  );

  // ── Analytics ─────────────────────────────────────────────────────────
  const { trackCardClick, trackChooseStart, trackZeroResults } =
    useBlueprintAnalytics();

  // ── Data ──────────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<TemplateEntityV1beta3[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    catalogApi
      .getEntities({ filter: { kind: 'Template' } })
      .then(({ items }) => {
        if (!cancelled) {
          setTemplates(
            items.filter(isTemplateEntity).filter(t => !isHiddenBlueprint(t)),
          );
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  // ── Filter / search state ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  // Multi-select tag filter (replaces the old single curated-category filter).
  // A template matches only if it carries every selected tag (AND semantics).
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  // viewMode (grid/list) and grouping (categorised/alphabetical) are persisted
  // per-user in localStorage so the chosen layout survives reloads.
  const [viewMode, setViewMode] = usePersistedChoice<ViewMode>(
    VIEW_MODE_KEY,
    ['grid', 'list'],
    'grid',
  );
  const [grouping, setGrouping] = usePersistedChoice<Grouping>(
    GROUPING_KEY,
    ['categorised', 'alphabetical'],
    'categorised',
  );

  // ── Derived: every distinct tag across all templates (filter options) ──
  const allTags = useMemo(
    () =>
      Array.from(new Set(templates.flatMap(tagsOf))).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    [templates],
  );

  // ── Derived: filtered templates (search + tag filter) ─────────────────
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return templates.filter(t => {
      const tags = tagsOf(t);
      // Tag filter — template must carry EVERY selected tag (AND).
      if (
        selectedTags.length > 0 &&
        !selectedTags.every(tag => tags.includes(tag))
      ) {
        return false;
      }
      // Free-text search across name, description and tags.
      if (q) {
        const matches =
          (t.metadata.title ?? t.metadata.name).toLowerCase().includes(q) ||
          (t.metadata.description ?? '').toLowerCase().includes(q) ||
          tags.some(tag => tag.toLowerCase().includes(q));
        if (!matches) return false;
      }
      return true;
    });
  }, [templates, searchQuery, selectedTags]);

  // ── Derived: grouped by curated categories ────────────────────────────
  const grouped = useMemo(
    () =>
      BLUEPRINT_CATEGORIES.map(cat => ({
        category: cat,
        templates: filtered.filter(t => tagsOf(t).includes(cat.id)),
      })),
    [filtered],
  );

  // Templates that carry NO curated category tag → "Other"
  const otherTemplates = useMemo(() => {
    const categoryIds = new Set(BLUEPRINT_CATEGORIES.map(c => c.id));
    return filtered.filter(t => !tagsOf(t).some(tag => categoryIds.has(tag)));
  }, [filtered]);

  // ── Derived: flat alphabetical list (for the alphabetical view) ───────
  //
  // De-duplicate by catalog ref (a template can carry several category tags
  // but must appear once) then sort case-insensitively by display name.
  const alphabetical = useMemo(() => {
    const seen = new Set<string>();
    const unique: TemplateEntityV1beta3[] = [];
    for (const t of filtered) {
      const key = `${t.metadata.namespace ?? 'default'}/${t.metadata.name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(t);
    }
    return unique.sort((a, b) =>
      templateDisplayName(a).localeCompare(templateDisplayName(b), undefined, {
        sensitivity: 'base',
      }),
    );
  }, [filtered]);

  // ── Derived: total (de-duplicated) count ──────────────────────────────
  const totalUnique = useMemo(() => {
    const seen = new Set<string>();
    for (const t of filtered) {
      seen.add(`${t.metadata.namespace ?? 'default'}/${t.metadata.name}`);
    }
    return seen.size;
  }, [filtered]);

  // ── Derived: typo-detection (dev only) ────────────────────────────────
  const unknownCategoryLikeTags = useMemo(() => {
    if (process.env.NODE_ENV === 'production') return [];
    const categoryIds = BLUEPRINT_CATEGORIES.map(c => c.id);
    const seen = new Set<string>();
    const result: { tag: string; closest: string; templateName: string }[] = [];

    for (const t of templates) {
      for (const tag of tagsOf(t)) {
        const mapKey = `${tag}::${t.metadata.name}`;
        if (seen.has(mapKey)) continue;
        seen.add(mapKey);
        if (categoryIds.includes(tag)) continue;
        if (/^[a-z][a-z-]*[a-z]$/.test(tag)) {
          for (const catId of categoryIds) {
            if (levenshtein(tag, catId) === 1) {
              result.push({
                tag,
                closest: catId,
                templateName: t.metadata.name,
              });
              break;
            }
          }
        }
      }
    }

    return result;
  }, [templates]);

  // ── Side-effect: warn in console when typos are detected (dev only) ──────
  useEffect(() => {
    if (unknownCategoryLikeTags.length === 0) return;
    // oxlint-disable-next-line no-console
    console.warn(
      '[Blueprints] Possible category tag typos detected:\n',
      unknownCategoryLikeTags
        .map(
          r =>
            `  "${r.tag}" (template: ${r.templateName}) - did you mean "${r.closest}"?`,
        )
        .join('\n'),
    );
  }, [unknownCategoryLikeTags]);

  // ── Navigation ────────────────────────────────────────────────────────
  const handleChoose = useCallback(
    (template: TemplateEntityV1beta3) => {
      const ns = template.metadata.namespace ?? 'default';
      // Build the entity ref in the standard Backstage format.
      const templateRef = `template:${ns}/${template.metadata.name}`;
      // Resolve the first curated category this template belongs to.
      const tags = tagsOf(template);
      const category =
        BLUEPRINT_CATEGORIES.find(c => tags.includes(c.id))?.id ?? 'other';

      // Emit analytics before navigating so the event is captured even if
      // the navigate() call triggers an immediate unmount.
      trackCardClick(templateRef, category);
      trackChooseStart(templateRef);

      navigate(
        selectedTemplateRoute({
          namespace: ns,
          templateName: template.metadata.name,
        }),
      );
    },
    [navigate, selectedTemplateRoute, trackCardClick, trackChooseStart],
  );

  // ── Zero-results telemetry (debounced 500 ms) ─────────────────────────
  //
  // Tracks the last filter combo we emitted so we don't spam events while
  // the user is still typing. The ref resets to null whenever the filtered
  // count rises above zero so that a subsequent zero-results state with the
  // same inputs re-emits (e.g. type → backspace → retype).
  const lastZeroEmitRef = useRef<{
    query: string;
    tags: string;
    viewMode: 'grid' | 'list';
  } | null>(null);

  useEffect(() => {
    if (loading || error || totalUnique > 0) {
      // Results present (or still loading): reset the dedup guard so a later
      // zero-results state will emit even if the inputs are the same.
      lastZeroEmitRef.current = null;
      return undefined;
    }

    // Serialise the selected tags (sorted) so the dedup comparison is stable
    // regardless of selection order.
    const tagsKey = [...selectedTags].sort().join(',');
    const combo = { query: searchQuery, tags: tagsKey, viewMode };
    const last = lastZeroEmitRef.current;

    // Skip if we already emitted for this exact filter combination.
    if (
      last &&
      last.query === combo.query &&
      last.tags === combo.tags &&
      last.viewMode === combo.viewMode
    ) {
      return undefined;
    }

    const timer = setTimeout(() => {
      trackZeroResults(combo);
      lastZeroEmitRef.current = combo;
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [
    loading,
    error,
    totalUnique,
    searchQuery,
    selectedTags,
    viewMode,
    trackZeroResults,
  ]);

  // ── Tab navigation ────────────────────────────────────────────────────
  // ── Render ────────────────────────────────────────────────────────────
  return (
    <FullPage>
      <BlueprintsHeader
        subtitle="Reusable templates for components, workflows and infra"
        pageTitleOverride="Blueprints"
      />

      <Container py="6">
        {/* ── Toolbar ──────────────────────────────────────────────── */}
        <Flex align="center" gap="4" mb="4" style={{ flexWrap: 'wrap' }}>
          {/* Search */}
          <Box style={{ flex: 1, minWidth: 220 }}>
            <SearchField
              aria-label="Search templates"
              placeholder="Search templates…"
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </Box>

          {/* Tag filter (multi-select) */}
          <Box style={{ width: 260 }}>
            <Select
              aria-label="Tags"
              selectionMode="multiple"
              search
              placeholder="Filter by tag"
              options={allTags.map(tag => ({ value: tag, label: tag }))}
              value={selectedTags}
              onChange={keys =>
                setSelectedTags(Array.isArray(keys) ? keys.map(String) : [])
              }
            />
          </Box>

          {/* Grouping toggle — categorised vs flat alphabetical */}
          <Box ml="auto">
            <ToggleButtonGroup
              aria-label="Group templates"
              selectionMode="single"
              disallowEmptySelection
              selectedKeys={[grouping]}
              onSelectionChange={keys => {
                const [next] = Array.from(keys);
                if (next === 'categorised' || next === 'alphabetical') {
                  setGrouping(next);
                }
              }}
            >
              <ToggleButton
                id="categorised"
                aria-label="Group templates by category"
                iconStart={<RiShapesLine size={16} />}
              />
              <ToggleButton
                id="alphabetical"
                aria-label="Sort templates alphabetically"
                iconStart={<RiSortAlphabetAsc size={16} />}
              />
            </ToggleButtonGroup>
          </Box>

          {/* View toggle */}
          <ToggleButtonGroup
            aria-label="Template view"
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={[viewMode]}
            onSelectionChange={keys => {
              const [next] = Array.from(keys);
              if (next === 'grid' || next === 'list') setViewMode(next);
            }}
          >
            <ToggleButton
              id="grid"
              aria-label="Switch to grid view"
              iconStart={<RiGridLine size={16} />}
            />
            <ToggleButton
              id="list"
              aria-label="Switch to list view"
              iconStart={<RiListCheck size={16} />}
            />
          </ToggleButtonGroup>

          {/*
           * "Edit a template" CTA — lives on the Templates tab toolbar, where
           * it's contextually relevant and doesn't compete with page chrome.
           */}
          <ButtonLink
            href="/create/edit"
            variant="secondary"
            iconStart={<RiPencilLine size={16} />}
          >
            Edit a template
          </ButtonLink>
        </Flex>

        {/*
         * Favorites & recently-used rows - load independently on mount.
         * Rendered between the toolbar and the main grid so they appear
         * above the curated-category sections. Each row fails silently.
         */}
        <FavoritesAndRecent viewMode={viewMode} />

        {/* ── Loading / error states ────────────────────────────────── */}
        {loading && (
          <Box role="progressbar" aria-label="Loading templates">
            <Skeleton width="100%" height={4} rounded />
          </Box>
        )}
        {!loading && error && (
          <Alert
            role="alert"
            status="danger"
            icon
            title="Unable to load templates"
            description={error.message}
          />
        )}

        {/* ── Dev-only typo panel ───────────────────────────────────── */}
        {!loading &&
          !error &&
          process.env.NODE_ENV !== 'production' &&
          unknownCategoryLikeTags.length > 0 && (
            <Box mb="4">
              <Alert
                status="warning"
                icon
                title="Dev: possible category tag typos"
                description={unknownCategoryLikeTags
                  .map(
                    entry =>
                      `“${entry.tag}” on “${entry.templateName}” - did you mean “${entry.closest}”?`,
                  )
                  .join(' · ')}
              />
            </Box>
          )}

        {/* ── Total count ───────────────────────────────────────────── */}
        {!loading && !error && (
          <Text
            as="p"
            color="secondary"
            style={{ marginBottom: 'var(--bui-space-4)' }}
          >
            {totalUnique === 0
              ? 'No templates match your search.'
              : `${totalUnique} template${totalUnique !== 1 ? 's' : ''}`}
          </Text>
        )}

        {/* ── Alphabetical view ─────────────────────────────────────── */}
        {!loading && !error && grouping === 'alphabetical' && (
          <TemplateSection
            category={{
              id: 'all',
              label: 'All templates',
              description: 'Every matching template, sorted alphabetically.',
            }}
            templates={alphabetical}
            viewMode={viewMode}
            onChoose={handleChoose}
          />
        )}

        {/* ── Grouped sections ─────────────────────────────────────── */}
        {/*
         * Hide empty curated-category sections. Empty sections produce visual
         * noise and imply broken data when in fact the curated categories are
         * aspirational - tags are added to templates over time.
         */}
        {!loading &&
          !error &&
          grouping === 'categorised' &&
          grouped
            .filter(g => g.templates.length > 0)
            .map(({ category, templates: catTemplates }) => (
              <TemplateSection
                key={category.id}
                category={category}
                templates={catTemplates}
                viewMode={viewMode}
                onChoose={handleChoose}
              />
            ))}

        {/* ── "Other" section ───────────────────────────────────────── */}
        {!loading &&
          !error &&
          grouping === 'categorised' &&
          otherTemplates.length > 0 && (
            <TemplateSection
              category={{
                id: 'other',
                label: 'Other',
                description:
                  'Templates not yet assigned to a curated category.',
              }}
              templates={otherTemplates}
              viewMode={viewMode}
              onChoose={handleChoose}
            />
          )}
      </Container>
    </FullPage>
  );
}
